import { useEffect, useRef } from 'react';

function generateBolt(x1, y1, x2, y2, segments) {
  const pts = [[x1, y1]];
  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const mx = x1 + (x2 - x1) * t + (Math.random() - 0.5) * 60;
    const my = y1 + (y2 - y1) * t + (Math.random() - 0.5) * 50;
    pts.push([mx, my]);
  }
  pts.push([x2, y2]);
  return pts.map(p => p.join(',')).join(' ');
}

function spawnParticles(x, y) {
  for (let i = 0; i < 12; i++) {
    const p = document.createElement('div');
    p.className = 'particle';
    const angle = (Math.PI * 2 * i) / 12;
    const dist = 20 + Math.random() * 30;
    p.style.cssText = `left:${x}px;top:${y}px;background:#ffd700;--dx:${Math.cos(angle) * dist}px;--dy:${Math.sin(angle) * dist}px;`;
    document.body.appendChild(p);
    setTimeout(() => p.remove(), 800);
  }
}

export default function LightningVFX({ lastAttack }) {
  const lastTs = useRef(Date.now() / 1000); // Initialize to current time — skip stale attacks on load

  useEffect(() => {
    if (!lastAttack || !lastAttack.ts) return;
    if (lastAttack.ts <= lastTs.current) return;
    lastTs.current = lastAttack.ts;

    // Small delay to ensure DOM has rendered the cards
    setTimeout(() => {
      const attackerCard = document.getElementById('card-' + lastAttack.attacker);
      const enemyCards = document.querySelectorAll('.enemy-card');
      let targetCard = null;
      enemyCards.forEach(card => {
        const nameEl = card.querySelector('.enemy-name');
        if (nameEl && nameEl.textContent.trim().startsWith(lastAttack.target)) targetCard = card;
      });
      if (!attackerCard) return;

      const isCrit = lastAttack.crit;
      const fromRect = attackerCard.getBoundingClientRect();
      let x1, y1, x2, y2;
      const isMobileView = window.innerWidth <= 1024;

      if (!isMobileView && targetCard && targetCard.getBoundingClientRect().width > 0) {
        // Desktop: attacker → enemy
        const toRect = targetCard.getBoundingClientRect();
        x1 = fromRect.right;
        y1 = fromRect.top + fromRect.height / 2;
        x2 = toRect.left;
        y2 = toRect.top + toRect.height / 2;
      } else if (isMobileView) {
        // Mobile: strike within player card
        x1 = fromRect.left + fromRect.width * 0.3;
        y1 = fromRect.top + 10;
        x2 = fromRect.right - 10;
        y2 = fromRect.bottom - 20;
      } else {
        // Desktop but enemy not visible — skip
        return;
      }

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.classList.add('lightning-svg');
      const color = isCrit ? '#ffd700' : '#d4aaff';
      const coreColor = '#ffffff';
      const cls = isCrit ? 'bolt-crit' : 'bolt';
      const branches = isCrit ? 5 : 3;

      let bolts = '';
      const mainPts = generateBolt(x1, y1, x2, y2, isCrit ? 12 : 9);
      bolts += `<polyline class="${cls}" points="${mainPts}" fill="none" stroke="${coreColor}" stroke-width="${isCrit ? 4 : 2.5}" filter="url(#glow)"/>`;
      bolts += `<polyline class="${cls}" points="${mainPts}" fill="none" stroke="${color}" stroke-width="${isCrit ? 7 : 4}" opacity="0.6" filter="url(#glow)"/>`;

      for (let b = 0; b < branches; b++) {
        const t = 0.2 + Math.random() * 0.6;
        const bx = x1 + (x2 - x1) * t + (Math.random() - 0.5) * 20;
        const by = y1 + (y2 - y1) * t + (Math.random() - 0.5) * 20;
        const endX = bx + (Math.random() - 0.5) * 80;
        const endY = by + (Math.random() - 0.5) * 60;
        const branchPts = generateBolt(bx, by, endX, endY, 4);
        bolts += `<polyline class="${cls}" points="${branchPts}" fill="none" stroke="${color}" stroke-width="${isCrit ? 2 : 1.5}" opacity="0.7" filter="url(#glow)"/>`;
      }

      const mainPts2 = generateBolt(x1 + 2, y1 + 2, x2 - 2, y2 - 2, isCrit ? 10 : 7);
      bolts += `<polyline class="${cls}" points="${mainPts2}" fill="none" stroke="${color}" stroke-width="${isCrit ? 3 : 1.5}" opacity="0.5" filter="url(#glow)"/>`;

      svg.innerHTML = `${bolts}<defs><filter id="glow"><feGaussianBlur stdDeviation="${isCrit ? 8 : 5}" result="g"/><feMerge><feMergeNode in="g"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>`;
      document.body.appendChild(svg);

      if (isCrit) spawnParticles(x2, y2);
      setTimeout(() => svg.remove(), isCrit ? 2500 : 1500);
    }, 100);
  }, [lastAttack]);

  return null;
}
